<template>
    <div class="w-100">
        <pc-table
            id="ItemsInFolderTable"
            ref="libraryFolderTable"
            v-bind:items="items"
            v-bind:columns="columns"
            v-bind:initial-sort="initialSort"
            v-bind:items-per-page="20"
            v-bind:hide-actions="hideAllActions || selectorMode"
            v-bind:show-actions-per-item="showActionsPerRow"
            v-bind:hide-search="selectorMode"
            v-bind:empty-text="emptyText"
            v-bind:context-menu-actions="rowActions"
            key-prop="Id"
            preserve-page
            class="mb-2 w-100">
            <template v-slot:Name="row">
                <div v-bind:class="getNameCellClassObj(row)">
                    <div v-bind:class="getIconClass(row)" />
                    <a
                        v-if="!isFileItem(row)"
                        class="ms-2"
                        href="#"
                        v-on:click="$emit('open-folder', row)">
                        {{ row.Name }}
                    </a>
                    <div
                        v-else-if="selectorMode"
                        class="ms-2">
                        <input
                            v-bind:id="selectorId(row)"
                            v-bind:disabled="isUnavailable(row)"
                            type="radio"
                            name="ItemSelector"
                            class="mt-1"
                            v-bind:checked="selectedItemId === row.Id"
                            v-on:click="selectItem(row)" />
                        <label
                            v-bind:for="selectorId(row)"
                            class="mb-0 d-inline">
                            {{ row.Name }}
                        </label>
                    </div>
                    <div
                        v-else
                        class="ms-2">
                        {{ row.Name }}
                    </div>
                </div>
            </template>
            <template v-slot:Published="row">
                <div v-bind:class="getTimestampCellClassObj(row)">
                    {{ getTimestampDisplay(row) }}
                </div>
            </template>
            <template v-slot:actionsRow="row">
                <!-- Any updates to this Access Url div must also be made to rowActions.accessUrl -->
                <div
                    v-if="isFileItem(row)"
                    class="mb-1">
                    {{ urlLabel }}
                    <a
                        v-bind:href="getLibraryAccessUrl(row)"
                        target="_blank">
                        {{ getLibraryAccessUrl(row) }}
                    </a>
                </div>
                <div class="d-flex flex-wrap">
                    <pc-button
                        v-if="rowActions.rename.visible(row)"
                        v-bind:id="rowActions.rename.id"
                        class="me-2 mt-1"
                        v-on:click="rowActions.rename.action(row)">
                        {{ rowActions.rename.text }}
                    </pc-button>
                    <pc-button
                        v-bind:id="rowActions.delete.id"
                        class="me-2 mt-1"
                        v-on:click="rowActions.delete.action(row)">
                        {{ rowActions.delete.text }}
                    </pc-button>
                    <pc-button
                        v-if="rowActions.history.visible(row)"
                        v-bind:id="rowActions.history.id"
                        class="me-2 mt-1"
                        v-on:click="rowActions.history.action(row)">
                        {{ rowActions.history.text }}
                    </pc-button>
                    <pc-button
                        v-if="rowActions.newVersion.visible(row)"
                        v-bind:id="rowActions.newVersion.id"
                        class="mt-1"
                        v-bind:primary="rowActions.newVersion.primary"
                        v-on:click="rowActions.newVersion.action(row)">
                        {{ rowActions.newVersion.text }}
                    </pc-button>
                    <!-- TODO-TCM(#41362) See if we should incorporate this message into the context menu -->
                    <div
                        v-if="row.IsSample"
                        class="ms-2 mt-2 fst-italic">
                        {{ sampleMessage }}
                    </div>
                </div>
            </template>
        </pc-table>
    </div>
</template>
<script>
    import { KEYS, NAMESPACE } from "./i18n/libraryFolder.i18n";
    import { mapI18n } from "Util/i18n";
    import { getClientTimestampTableColumn } from "Util/clientDateTime";

    export default {
        name: "LibraryFolder",
        props: {
            folderName: { type: String, required: true },
            items: { type: Array, required: true },
            hideAllActions: { type: Boolean, required: false, default: false },
            libraryAccessUrlBase: { type: String, required: false, default: null },
            selectorMode: { type: Boolean, required: false, default: false },
            unavailableItemIds: { type: Array, required: false, default: () => [] },
            existingSelectedItemId: { type: String, required: false, default: null }
        },
        emits: ["open-folder", "rename-item", "delete-item", "show-file-history", "new-file-version", "select-file"],
        data() {
            return {
                initialSort: [
                    [1, "asc"],
                    [0, "asc"]
                ],
                selectedItemId: this.existingSelectedItemId
            };
        },
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            columns() {
                return [
                    {
                        header: this.nameColumnHeader,
                        property: "Name",
                        orderable: true,
                        searchable: true,
                        visible: true
                    },
                    getClientTimestampTableColumn(this.publishedColumnHeader, "Published")
                ];
            },
            rowActions() {
                return {
                    rename: {
                        visible: (row) => !row.IsSample,
                        id: "RenameItemButton",
                        action: (row) => this.$emit("rename-item", row),
                        text: this.renameText,
                        iconName: "pencil"
                    },
                    delete: {
                        id: "DeleteItemButton",
                        action: (row) => this.$emit("delete-item", row),
                        text: this.deleteText,
                        iconName: "trash3"
                    },
                    history: {
                        visible: (row) => this.isFileItem(row) && !row.IsSample,
                        id: "ItemHistoryButton",
                        action: (row) => this.$emit("show-file-history", row),
                        text: this.historyText,
                        iconName: "clock-history"
                    },
                    newVersion: {
                        visible: (row) => this.isFileItem(row) && !row.IsSample,
                        id: "ItemNewVersionButton",
                        primary: true,
                        action: (row) => this.$emit("new-file-version", row),
                        text: this.uploadNewVersionText,
                        iconName: "cloud-upload"
                    },
                    accessUrl: {
                        visible: (row) => this.isFileItem(row),
                        id: "CopyUrlButton",
                        action: (row) => navigator.clipboard.writeText(this.getLibraryAccessUrl(row)),
                        text: this.copyAccessUrlLabel,
                        iconName: "clipboard"
                    }
                };
            }
        },
        methods: {
            getIconClass(row) {
                switch (row.FileType) {
                    case 0:
                        return "item-icon folder-icon";
                    case 1:
                        return "item-icon document-icon";
                    case 2:
                        return "item-icon image-icon";
                    default:
                        return "item-icon";
                }
            },
            getLibraryAccessUrl(row) {
                let url = `${this.libraryAccessUrlBase}/${row.ContentPath}`;
                if (this.libraryAccessUrlBase && this.libraryAccessUrlBase.startsWith("https://localhost")) {
                    url += `?ProgramId=${this.$app.ProgramId}`;
                }
                return url;
            },
            isFileItem(row) {
                return row.FileType != 0;
            },
            showActionsPerRow(row) {
                return !this.selectorMode || this.isFileItem(row);
            },
            selectorId(row) {
                return row.Id + "-select";
            },
            selectItem(row) {
                this.selectedItemId = row.Id;
                this.$emit("select-file", row);
            },
            isUnavailable(row) {
                return this.unavailableItemIds.includes(row.Id);
            },
            getNameCellClassObj(row) {
                return {
                    "d-flex": true,
                    "align-items-center": true,
                    "text-body-secondary": this.isUnavailable(row),
                    "fst-italic": this.isUnavailable(row),
                    "library-folder-unavailable": this.isUnavailable(row)
                };
            },
            getTimestampCellClassObj(row) {
                return {
                    "text-body-secondary": this.isUnavailable(row),
                    "fst-italic": this.isUnavailable(row),
                    "library-folder-unavailable": this.isUnavailable(row)
                };
            },
            getTimestampDisplay(row) {
                if (!row.Published) {
                    return "";
                } else {
                    return row.Published.Display;
                }
            }
        }
    };
</script>
<style lang="scss">
    tr:has(div.library-folder-unavailable) {
        background-color: var(--bs-gray-100);
    }
    div.item-icon {
        background-position: center;
        background-repeat: no-repeat;
        width: 16px;
        height: 16px;
    }
    div.folder-icon {
        background-image: url("Images/icons/Folder.svg");
    }
    div.document-icon {
        background-image: url("Images/icons/Document.svg");
    }
    div.image-icon {
        background-image: url("Images/icons/Image.svg");
    }
</style>
